import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, AlertTriangle, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { Reveal, Stagger, item, SectionHeading } from "@/components/Reveal";
import { PageHero, CtaBanner } from "@/components/Sections";
import { SERVICE_ICONS } from "@/components/ServiceGrid";
import { Marquee } from "@/components/Marquee";

export default function ServicesPage() {
  const { t, to } = useLang();
  const s = t.services;
  return (
    <div data-testid="services-page">
      <PageHero eyebrow={s.hero.eyebrow} title={s.hero.title} sub={s.hero.sub} id="services" />
      <Marquee items={t.home.marquee} />

      <section className="container-x py-20 lg:py-28" data-testid="services-list-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <p className="eyebrow">{s.listTitle}</p>
              <ul className="mt-5 space-y-1 border-l border-slate-200">
                {s.items.map((it, i) => (
                  <li key={it.id}>
                    <a href={`#${it.id}`} className="-ml-px block border-l-2 border-transparent py-1.5 pl-4 text-sm text-slate-600 transition-colors hover:border-teal-500 hover:text-slate-900" data-testid={`services-anchor-${i + 1}`}>{it.title}</a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
          <div className="space-y-6 lg:col-span-9">
            {s.items.map((it, i) => {
              const Icon = SERVICE_ICONS[it.id];
              return (
                <Reveal key={it.id} y={24}>
                  <article id={it.id} className="scroll-mt-28 rounded-[2rem] border border-slate-200 bg-white p-8 transition-[border-color,box-shadow] hover:border-teal-300 hover:shadow-[0_30px_60px_-40px_rgba(15,23,42,0.35)] sm:p-10" data-testid={`service-detail-${i + 1}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-700"><Icon size={26} /></span>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{it.tagline}</p>
                          <h2 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">{it.title}</h2>
                        </div>
                      </div>
                      {it.optional && <span className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-500">{s.optionalLabel}</span>}
                    </div>
                    <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">{it.desc}</p>
                    <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                      {it.points.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-sm text-slate-700"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700"><Check size={12} /></span>{p}</li>
                      ))}
                    </ul>
                    {it.note && (
                      <p className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900" data-testid={`service-note-${it.id}`}>
                        <AlertTriangle size={18} className="mt-0.5 shrink-0" />{it.note}
                      </p>
                    )}
                    <Link to={to("contact")} className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-teal-700 transition-colors hover:text-teal-900" data-testid={`service-cta-${i + 1}`}>{t.nav.quote} <ArrowRight size={14} /></Link>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-navy py-20 text-white lg:py-28" data-testid="services-process-section">
        <div className="container-x relative">
          <SectionHeading eyebrow={s.process.eyebrow} title={s.process.title} light id="services-process" />
          <Stagger className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4" stagger={0.1}>
            {s.process.steps.map((st, i) => (
              <motion.div key={st.title} variants={item} className="relative rounded-3xl border border-white/10 bg-white/5 p-7 backdrop-blur" data-testid={`services-process-step-${i + 1}`}>
                <span className="font-mono text-sm text-teal-300">0{i + 1}</span>
                <h3 className="mt-4 font-display text-xl font-bold">{st.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{st.desc}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="container-x py-20 lg:py-28" data-testid="services-custom-section">
        <div className="grid items-center gap-10 rounded-[2.5rem] border border-teal-200 bg-gradient-to-br from-teal-50 via-white to-sky-50 p-10 lg:grid-cols-12 lg:p-16">
          <div className="lg:col-span-8">
            <SectionHeading eyebrow={s.custom.eyebrow} title={s.custom.title} sub={s.custom.desc} id="services-custom" />
          </div>
          <Reveal className="lg:col-span-4 lg:text-right" delay={0.1}>
            <Link to={to("contact")} className="btn-primary" data-testid="services-custom-cta">{s.custom.cta} <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>

      <CtaBanner title={s.cta.title} sub={s.cta.sub} btn1={s.cta.btn} to1={to("contact")} id="services-cta" />
    </div>
  );
}
